import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { select, Store } from '@ngrx/store';
import {
  authenticateByContractNumber,
  logout,
  logoutFailure,
  logoutSuccess,
  selectAuthenticationQrDataUri,
  selectContractNumber,
  selectIsAuthenticationInProgress,
  selectIsAuthenticationUnsuccessful
} from '@qvs/core/auth';
import { TranslatePipe } from '@ngx-translate/core';
import { SvgIconComponent } from '~/shared/form-elements/components/svg-icon/svg-icon.component';
import { LoginPageHeaderComponent } from '@ebanking/features/login/login-page-header/login-page-header.component';
import { RegistrationErrorComponent } from '@ebanking/features/login/registration-error/registration-error.component';
import { Actions, ofType } from '@ngrx/effects';

@Component({
  selector: 'qvs-login-qr',
  standalone: true,
  imports: [CommonModule, TranslatePipe, SvgIconComponent, LoginPageHeaderComponent, RegistrationErrorComponent],
  templateUrl: './login-qr.component.html',
  styleUrls: ['./login-qr.component.scss']
})
export class LoginQrComponent implements OnDestroy {
  dataUri?: string;
  isDataUriLoading = false;
  isAuthenticationInProgress$: Observable<boolean>;
  contractNumber$: Observable<string | null>;
  isQRCodeExpired$: Observable<boolean>;
  isGenerateNewQRCodeButtonDisabled = false;

  private readonly dataUri$: Observable<string>;
  private readonly destroyed$ = new Subject<void>();

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions
  ) {
    this.dataUri$ = this.store.pipe(
      select(selectAuthenticationQrDataUri),
      filter((qr) => !!qr),
      takeUntil(this.destroyed$)
    );
    this.dataUri$.subscribe((qr) => {
      this.dataUri = qr;
      this.isDataUriLoading = false;
    });

    this.isAuthenticationInProgress$ = this.store.pipe(select(selectIsAuthenticationInProgress), takeUntil(this.destroyed$));

    this.contractNumber$ = this.store.pipe(
      select(selectContractNumber),
      filter((result) => !!result),
      takeUntil(this.destroyed$)
    );

    this.isQRCodeExpired$ = this.store.pipe(select(selectIsAuthenticationUnsuccessful), takeUntil(this.destroyed$));
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  generateNewQRCode() {
    this.contractNumber$.pipe(take(1), takeUntil(this.destroyed$)).subscribe((contractNumber) => {
      if (!contractNumber) {
        return;
      }

      this.isDataUriLoading = true;
      this.isGenerateNewQRCodeButtonDisabled = true;

      // XSRF cookie is invalid after QR code expiration, so we need to logout before we can authenticate again
      this.actions$.pipe(ofType(logoutSuccess, logoutFailure), take(1), takeUntil(this.destroyed$)).subscribe((action) => {
        if (action.type === logoutSuccess.type) {
          this.store.dispatch(authenticateByContractNumber({ contractNumber, contractNumberPrefixed: contractNumber }));
        }
        this.isGenerateNewQRCodeButtonDisabled = false;
      });

      this.store.dispatch(logout());
    });
  }

  get description$() {
    return this.isAuthenticationInProgress$.pipe(
      map((isAuthenticationInProgress) =>
        isAuthenticationInProgress ? 'ebanking.login.QR_INSTRUCTION' : 'ebanking.login.LOGIN_SUCCESSFUL'
      )
    );
  }

  get descriptionIcon$() {
    return this.isAuthenticationInProgress$.pipe(
      map((isAuthenticationInProgress) => (isAuthenticationInProgress ? 'assets/icons/scan-qr.svg' : 'assets/images/success.svg'))
    );
  }
}
